<template>
  <div class="c-hardened-images-list">
    <div class="selector">
      <div class="selector-inner">
        <h3 class="description" v-if="fields?.dropdownDescription?.value">
          {{ fields?.dropdownDescription?.value }}
        </h3>
        <div class="dropdown">
          <div
            class="dropdown-headline"
            @click="isOpen = !isOpen"
            :class="{
              open: isOpen
            }"
          >
            <div v-if="checkedCount === 0">Available Cloud Service Providers</div>
            <div v-else>
              (<span>{{ checkedCount }}</span
              >) Selected Marketplaces
            </div>
          </div>
          <div v-show="isOpen">
            <div class="options">
              <div
                class="option"
                v-for="item in [...$options.computed.columnHeader].sort((a, b) =>
                  a?.$props?.fields?.title?.value.localeCompare(b?.$props?.fields?.title?.value)
                )"
                :key="item?.$props?.key"
              >
                <label
                  class="c-checkbox"
                  :class="{
                    disabled:
                      checkedCount >= fields.maxMarketplaces.value &&
                      !tempSelectedColumns.includes(item.$props?.fields?.marketplace?.value)
                  }"
                >
                  <input
                    type="checkbox"
                    :value="item.$props?.fields?.marketplace?.value"
                    v-model="tempSelectedColumns"
                    :disabled="
                      checkedCount >= fields.maxMarketplaces.value &&
                      !tempSelectedColumns.includes(item.$props?.fields?.marketplace?.value)
                    "
                  />
                  {{ item?.$props?.fields?.title?.value }}
                </label>
              </div>
            </div>
            <div
              v-if="
                fields.maxMarketplaces.value != $options.computed.columnHeader.length &&
                checkedCount >= fields.maxMarketplaces.value
              "
              class="max-selection-note"
            >
              <span class="info"></span>
              <span>
                Maximum of {{ fields.maxMarketplaces.value }} selections at a time. Deselect a
                current selection to update table.
              </span>
            </div>
            <div v-if="checkedCount === 0" class="min-selection-note">
              <span class="info"></span>
              <span>
                Select up to ({{ fields.maxMarketplaces.value }}) Cloud Service Providers to view in
                the table below.
              </span>
            </div>
            <div class="buttons">
              <button
                class="apply"
                :class="{
                  disabled: checkedCount === 0
                }"
                @click="applySelection"
              >
                See Marketplaces
              </button>
              <button class="clear" v-if="checkedCount != 0" @click="clearSelection">
                Clear selections
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div
      class="table"
      :class="{
        empty: checkedCount === 0
      }"
    >
      <template v-if="!isEditing">
        <div
          v-if="selectedColumns.length != 0"
          class="tr th column-headers"
          :class="{ editing: isEditing }"
        >
          <div class="td"></div>
          <template
            v-for="(column, index) in $options.computed.columnHeader"
            :key="`column${index}`"
          >
            <component
              v-if="selectedColumns.includes(column.$props?.fields?.marketplace?.value)"
              :is="column"
            />
          </template>
        </div>
        <!-- Empty - Default Content -->
        <div v-else class="tr th column-headers">
          <div class="td"></div>
          <div class="td default">Select at least (1) Cloud Service Provider to review.</div>
        </div>
      </template>
      <template v-else>
        <div class="tr th column-headers" :class="{ editing: isEditing }">
          <div class="td"></div>
          <component
            v-for="(column, index) in $options.computed.columnHeader"
            :is="column"
            :key="`column${index}`"
          />
        </div>
      </template>

      <div v-if="loading">
        <p>Loading...</p>
      </div>

      <div class="showBackToTop" v-else>
        <template v-for="technology in technologyData.technologies" :key="technology.name">
          <div class="tr technology">
            <div class="td">
              <h3>{{ technology.name }}</h3>
            </div>
            <template v-for="col in selectedColumns" :key="`tech-${technology.name}-${col}`">
              <div class="td"></div>
            </template>
            <!-- Empty TD if no selected Marketplaces -->
            <div v-if="selectedColumns.length === 0" class="td"></div>
          </div>

          <template v-for="imageLevel in technology.imageLevels" :key="imageLevel.name">
            <div class="tr level">
              <div class="td">
                <a
                  class="tooltip"
                  v-if="fields?.tooltipLevel1?.value && imageLevel.name === 'Level 1'"
                  :tooltipHI="fields.tooltipLevel1.value"
                  >{{ imageLevel.name }}</a
                >
                <a
                  class="tooltip"
                  v-else-if="fields?.tooltipLevel2?.value && imageLevel.name === 'Level 2'"
                  :tooltipHI="fields.tooltipLevel2.value"
                  >{{ imageLevel.name }}</a
                >
                <a
                  class="tooltip"
                  v-else-if="fields?.tooltipLevelStig?.value && imageLevel.name === 'STIG'"
                  :tooltipHI="fields.tooltipLevelStig.value"
                  >{{ imageLevel.name }}</a
                >
                <span v-else>{{ imageLevel.name }}</span>
              </div>
              <template v-for="col in selectedColumns" :key="`level-${imageLevel.name}-${col}`">
                <div class="td"></div>
              </template>
              <!-- Empty TD if no selected Marketplaces -->
              <div v-if="selectedColumns.length === 0" class="td"></div>
            </div>

            <template v-for="image in imageLevel.images" :key="image.name">
              <div v-if="selectedColumns.length != 0" class="tr">
                <div class="td">{{ image.name }}</div>
                <template v-for="provider in image.providers" :key="provider.name">
                  <div class="td" v-if="selectedColumns.includes(provider.name)">
                    <div class="provider">
                      <a
                        v-if="provider.locationUrl?.trim()"
                        class="link external-link"
                        :href="provider.locationUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span v-if="provider.name === 'AWS Marketplace'">Launch</span>
                        <span v-else>Deploy</span>
                      </a>
                      <span v-if="provider.hasAic" class="aic"> (Also ICMP and ESC) </span>
                    </div>
                  </div>
                </template>
              </div>
              <div v-else class="tr">
                <div class="td">{{ image.name }}</div>
                <div class="td default">N/A</div>
              </div>
            </template>
          </template>
        </template>
      </div>
    </div>
    <button v-if="showBackToTop" class="back-to-top" @click="scrollToTop">Back to Top</button>
  </div>
</template>

<script>
import { getCurrentInstance, reactive, ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { HardenedImageQuery } from './CIS-Hardened-Images-List.graphql';
import { useQuery } from '@vue/apollo-composable';
import { useCookies } from 'vue3-cookies';
import findParentComponentByName from '../../lib/findParentComponentByName';

import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css'; // optional for styling

export default {
  name: 'CIS-Hardened-Images-List',
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },
  withPlaceholder() {
    return {
      placeholders: [
        {
          placeholder: 'jss-hardened-images-column-header',
          computedPropName: 'columnHeader'
        }
      ]
    };
  },
  computed: {
    isEditing() {
      const instance = getCurrentInstance();
      return !!instance?.appContext?.config?.globalProperties?.$jss?.sitecoreContext()?.pageEditing;
    }
  },
  setup(props) {
    const variables = () => {};
    const queryOptions = () => {
      return {
        prefetch: true
      };
    };

    const { result, loading, error, onResult } = useQuery(
      HardenedImageQuery,
      variables,
      queryOptions
    );

    const technologyData = reactive({
      technologies: []
    });

    const selectedMarketplaces = computed(() => {
      const items = props.rendering?.placeholders?.['jss-hardened-images-column-header'] || [];
      return items
        .filter((item) => item.fields?.selectedMarketplaces?.value === true)
        .map((item) => item.fields?.marketplace?.value)
        .filter(Boolean);
    });

    const selectedColumns = ref(
      selectedMarketplaces.value.slice(0, props.fields.maxMarketplaces.value)
    );

    const tempSelectedColumns = ref([
      ...selectedColumns.value.slice(0, props.fields.maxMarketplaces.value)
    ]);

    const isOpen = ref(true);
    const checkedCount = computed(() => tempSelectedColumns.value.length);

    const applySelection = () => {
      if (checkedCount.value === 0) {
        return;
      }

      selectedColumns.value = [...tempSelectedColumns.value];
    };

    const clearSelection = () => {
      checkedCount.value = 0;
      selectedColumns.value = [];
      tempSelectedColumns.value = [];
    };

    const marketplace = computed(() => {
      return props.rendering?.placeholders?.['jss-hardened-images-column-header']
        ?.map((item) => item.fields?.marketplace?.value)
        .filter(Boolean);
    });

    const showBackToTop = ref(false);

    const scrollToTop = () => {
      const el = document.querySelector('.dropdown');
      if (!el) return;

      const bannerItem = document.querySelector('.c-banner');
      if (!bannerItem) return;

      const dropdownTop = el.getBoundingClientRect();
      const scrollTop = window.scrollY + dropdownTop.top - bannerItem.offsetHeight - 20;

      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    };

    const checkVisibility = () => {
      const el = document.querySelector('.showBackToTop');
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const isVisible = rect.top + 250 < window.innerHeight && rect.bottom > 0;

      showBackToTop.value = isVisible;
    };

    onMounted(() => {
      if (typeof window !== 'undefined') {
        window.addEventListener('scroll', checkVisibility);
        checkVisibility();
      }
    });

    onUnmounted(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', checkVisibility);
      }
    });

    watch(isOpen, (newVal) => {
      if (!newVal) {
        tempSelectedColumns.value = [...selectedColumns.value];
      }
    });

    onResult((queryResult) => {
      const cloudProviders = marketplace.value;
      let markerplaces = [];
      var allmarkerplaces = queryResult.data?.items?.children.map((c) => c.cloudProvider?.value);
      allmarkerplaces?.forEach((item) => {
        if (!markerplaces.includes(item)) {
          markerplaces.push(item);
        }
      });

      const cloudImages = queryResult.data?.items?.children
        ?.filter((c) => {
          return c.cloudProvider?.value;
        })
        .sort((a, b) => {
          const technologyDiff = a.technologyName?.value?.localeCompare(b.technologyName?.value);
          if (technologyDiff !== 0) {
            return technologyDiff;
          }

          const imageLevelDiff = a.imageLevel?.value?.localeCompare(b.imageLevel?.value);
          if (imageLevelDiff !== 0) {
            return imageLevelDiff;
          }

          const imageDiff = a.imageName?.value?.localeCompare(b.imageName?.value);
          if (imageDiff !== 0) {
            return imageDiff * -1;
          }

          const providerDiff =
            cloudProviders.indexOf(a.cloudProvider?.value) -
            cloudProviders.indexOf(b.cloudProvider?.value);
          if (providerDiff < 0) {
            return -1;
          }
          if (providerDiff > 0) {
            return 1;
          }
          return 0;
        });

      if (!cloudImages) return undefined;

      const aic = 'AWS Intelligence Community';
      const aicImages = queryResult.data?.items?.children?.filter(
        (c) => c.cloudProvider?.value === aic
      );

      const aws = 'AWS Marketplace';
      const hasAic = (image) => {
        return (
          image.cloudProvider?.value === aws &&
          aicImages.some(
            (i) =>
              i.imageName?.value === image.imageName?.value &&
              i.imageLevel?.value === image.imageLevel?.value
          )
        );
      };

      technologyData.technologies = [
        ...new Map(
          cloudImages.map((t) => [
            t.technologyName.value,
            {
              name: t.technologyName.value,
              imageLevels: [
                ...new Map(
                  cloudImages
                    .filter((c) => c.technologyName.value === t.technologyName.value)
                    .map((l) => [
                      l.imageLevel.value,
                      {
                        name: l.imageLevel.value,
                        images: [
                          ...new Map(
                            cloudImages
                              .filter(
                                (c) =>
                                  c.technologyName.value === t.technologyName.value &&
                                  c.imageLevel.value === l.imageLevel.value
                              )
                              .map((i) => [
                                i.imageName.value,
                                {
                                  name: i.imageName.value,
                                  providers: [
                                    ...new Map([
                                      ...new Map(
                                        cloudProviders.map((c) => [
                                          c,
                                          {
                                            name: c
                                          }
                                        ])
                                      ),
                                      ...cloudImages
                                        .filter(
                                          (c) =>
                                            c.technologyName.value === t.technologyName.value &&
                                            c.imageLevel.value === l.imageLevel.value &&
                                            c.imageName.value === i.imageName.value
                                        )
                                        .map((p) => [
                                          p.cloudProvider.value,
                                          {
                                            name: p.cloudProvider.value,
                                            locationUrl: p.locationUrl.value,
                                            hasAic: hasAic(p)
                                          }
                                        ])
                                    ]).values()
                                  ]
                                }
                              ])
                          ).values()
                        ]
                      }
                    ])
                ).values()
              ]
            }
          ])
        ).values()
      ];
    });

    const { cookies } = useCookies();

    return {
      result,
      loading,
      error,
      cookies,
      technologyData,
      selectedColumns,
      checkedCount,
      tempSelectedColumns,
      applySelection,
      clearSelection,
      isOpen,
      scrollToTop,
      showBackToTop
    };
  },
  updated() {
    var banner = this.cookies.get('banner');
    var column = document.querySelector('.column-headers');
    var bannerItem = document.querySelector('.c-banner');

    if (banner === null || banner != 'closed') {
      if (column && bannerItem) {
        var top = bannerItem.offsetHeight;
        column.style.top = top + 'px';
      }
    }

    tippy('[tooltipHI]', {
      content: (reference) =>
        reference.getAttribute('tooltipHI') + '<div class="tooltip-close">X</div>',
      allowHTML: true,
      interactive: true,
      interactiveDebounce: 75,
      maxWidth: 384,
      placement: 'top-start',
      touch: true,
      onCreate(instance) {
        instance.popper.querySelector('.tooltip-close').onclick = () => {
          instance.hide();
        };
      }
    });

    findParentComponentByName(this, 'Layout')?.externalLinks();
  }
};
</script>
<style lang="scss" scoped>
@import '~@/assets/scss/components/checkbox.scss';

label {
  cursor: pointer;
  &.disabled {
    color: gray;
    cursor: auto;
    input {
      cursor: auto;
    }
  }
}

.selector {
  background-color: $box-gray;
  margin-bottom: 4.8rem;
  padding: 3.2rem 0;

  &-inner {
    @include padding-x-thin;
  }

  .description {
    font-size: 1.8rem;
    line-height: 2.4rem;
    color: $dark-blue;
    margin: 0 0 2.4rem 0;

    @include desktop-up {
      font-size: 2.4rem;
      line-height: 3rem;
    }
  }

  .dropdown {
    display: flex;
    flex-flow: column nowrap;
    background: var(--Main-Palette-White, #fff);
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08), 0 6px 12px 2px rgba(0, 0, 0, 0.08);
    background-color: $white;
    padding: 2.4rem;

    &-headline {
      position: relative;
      cursor: pointer;
      font-weight: 400;

      div,
      span {
        font-size: 2.4rem;
        color: $dark-blue;
      }

      span {
        font-weight: 600;
      }

      &::after {
        content: '';
        position: absolute;
        top: 0.2rem;
        right: 0;
        width: 2rem;
        height: 2rem;
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none"><path d="M38.7773 28L19.8885 11L0.999565 28" stroke="%230086BF" stroke-width="2"/></svg>');
        background-repeat: no-repeat;
        background-size: contain;
        transform: rotate(180deg);

        @include desktop-up {
          top: -0.5rem;
          width: 4rem;
          height: 4rem;
        }
      }

      &.open::after {
        transform: rotate(0deg);
      }
    }

    .options {
      margin: 3.2rem 0;

      @include tablet-up {
        columns: 3 auto;
      }

      .option {
        label {
          display: flex;
          @include font-styles('Arial', 1.8rem, 200, normal, 2.2rem, 0, $gray);
          margin: 0;
          margin-bottom: 2rem;
          cursor: pointer;

          &.disabled {
            cursor: auto;
            color: $lite-gray;

            input {
              cursor: auto;
            }
          }

          input {
            cursor: pointer;
            margin-top: 0;
            border-color: $blue;
          }

          &.c-checkbox {
            gap: 1rem;

            input[type='checkbox'] {
              width: 2.5rem;
              height: 2.5rem;
              transform: translateY(0);

              &::before {
                width: 2.5rem;
                height: 2.5rem;
                background: $blue url(~@/assets/images/icon-checkmark-white.svg) 50% no-repeat;
                box-shadow: none;
              }
            }
          }
        }
      }
    }

    .info {
      display: inline-block;
      top: 0.3rem;
      position: relative;
      width: 1.6rem;
      height: 1.6rem;
      background-image: url(~@/assets/images/icon-info.svg);
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      margin-right: 0.5rem;
    }
  }
}

.table {
  display: flex;
  flex-flow: column nowrap;
  flex: 1 1 auto;
  @include padding-x-thin;

  .th {
    display: none;
    position: sticky;
    top: -0.1rem;
    z-index: 1;
    background: $white;
    margin-bottom: 2rem;
    box-shadow: 0 1.7rem 1.8rem 0 rgba(0, 0, 0, 0.08), 0 0.3rem 0.8rem 0 rgba(0, 0, 0, 0.08);

    @include desktop-up {
      margin-bottom: 0;
      box-shadow: none;
    }

    &.editing {
      position: relative;
      display: inline-block;
      top: auto;
      z-index: auto;
    }
  }

  .th > .td {
    display: block;
    flex-direction: column;
    white-space: normal;
    justify-content: center;
    padding: 2rem 1rem 1.5rem 1rem;
    text-align: center;
    border-top: solid 0.5rem $orange;
    border-bottom: none;

    @include desktop-up {
      padding: 3rem 2rem 2.4rem 2rem;
    }

    @include large-desktop-up {
      padding: 3rem 4rem 2.4rem 4rem;
    }

    &:first-child {
      border: none;
      display: none;

      @include desktop-up {
        display: block;
      }
    }
  }

  .tr {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    margin-top: 0.1rem;

    @include desktop-up {
      flex-flow: row nowrap;
    }

    &.th {
      flex-flow: row nowrap;
    }

    &.technology {
      border-radius: 0.5rem;
      background: linear-gradient(
        169deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 59, 92, 1) 20%,
        rgba(0, 134, 191, 1) 100%
      );
      margin-bottom: 0.7rem;

      @include desktop-up {
        border-radius: 0;
        background: none;
        background-color: $box-gray;
        margin-bottom: 0;
      }

      .td {
        display: none;

        &:first-child {
          display: block;
          text-align: left;
        }

        @include desktop-up {
          display: flex;
        }
      }

      @include desktop-up {
        display: flex;
      }

      h3 {
        margin: 0;
        padding: 0 0 0 0.6rem;
        color: $white;

        @include desktop-up {
          color: $dark-blue;
          padding-top: 2rem;
        }
      }
    }

    &.level {
      border-radius: 0.5rem;
      background-color: $box-gray;

      @include desktop-up {
        border-radius: 0;
        background-color: $white;
        margin-top: 0;
      }

      .td {
        display: none;

        &:first-child {
          display: block;
          padding: 1rem 2rem 3rem 2rem;
          text-align: left;

          @include desktop-up {
            padding: 0 2rem 2.7rem 2rem;
          }
        }

        @include desktop-up {
          display: flex;
        }
      }

      @include desktop-up {
        display: flex;
      }

      &:deep() {
        .tippy-box {
          left: 1rem;
        }

        .tippy-box[data-placement^='top-end'] {
          @include desktop-up {
            left: 1.5rem;
          }
        }
        .tippy-box[data-placement^='top-end'] > .tippy-arrow {
          @include desktop-up {
            left: 2.5rem !important;
            right: auto !important;
            transform: translate3d(0, 0, 0) !important;
          }

          &:before {
            border-width: 0.8rem 0.8rem 0 0 !important;
          }
        }
      }
    }
  }

  .td {
    display: flex;
    padding: 0 1rem;
    justify-content: center;
    border-left: dashed 0.1rem $divider;
    border-bottom: dashed 0.1rem #d3d3d3;
    padding-bottom: 1.5rem;

    @include desktop-up {
      flex-flow: row nowrap;
      flex-grow: 1;
      flex-basis: 0;
      word-break: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
      padding: 0 4rem 2rem 4rem;
      border-bottom: none;
    }

    &:first-child {
      border: none;
      justify-content: center;
      text-align: center;
      padding: 3rem 2rem;
      width: 100%;
      @include font-styles('Arial', 1.8rem, 500, normal, 2.4rem, 0, $dark-blue);

      @include desktop-up {
        flex-grow: 2;
        justify-content: left;
        text-align: left;
        padding: 0 2rem 2.5rem 2rem;
        @include font-styles('Arial', 2rem, 200, normal, 2.6rem, 0, $dark-blue);
      }

      .tooltip {
        @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0.1rem, $dark-blue);

        margin: 2.8rem 0 0 0;
        text-transform: uppercase;

        &:after {
          bottom: -0.3rem;

          @include desktop-up {
            bottom: -0.7rem;
          }
        }
      }
    }

    &:not(:first-child) {
      flex: 1;
    }

    &:last-child {
      border-right: dashed 0.1rem $divider;
    }

    .provider {
      text-align: center;

      .link {
        @include font-styles('Arial Narrow', 1.8rem, 600, normal, 2.2rem, 0, $blue);
        text-transform: uppercase;

        &:after {
          @include phone-only {
            display: none !important;
          }
        }

        span {
          position: relative;
          z-index: -1;
        }
      }
    }

    .aic {
      display: block;
      @include font-styles('Arial', 1.4rem, 200, normal, 1.8rem, 0, $dark-blue);
    }

    &.default {
      color: $lite-gray;
    }
  }

  .hardened-image {
    &:last-child {
      margin-bottom: 3rem;

      @include desktop-up {
        margin-bottom: 0;
      }

      .td {
        border-bottom: none;
      }
    }
  }

  &.empty {
    .column-headers,
    .technology,
    .showBackToTop {
      .td {
        &:first-child {
          flex-grow: 1;
        }
      }
    }
  }
}

.max-selection-note,
.min-selection-note {
  display: flex;
  margin-bottom: 2.4rem;
  @include font-styles('Arial', 1.4rem, 400, normal, 1.8rem, 0, $black);
}

.buttons {
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @include tablet-up {
    flex-direction: row;
  }

  .apply {
    border: none;
    display: inline-flex;
    min-width: 26rem;
    background-color: $orange;
    padding: 1.6rem 2rem 1.6rem 2rem;
    text-transform: uppercase;
    border-radius: 0.4rem;
    justify-content: center;
    align-items: center;
    @include tablet-down {
      min-width: 100%;
    }
    @include font-styles('Arial Narrow', 1.8rem, 600, normal, normal, 0, $white);

    &:after {
      content: '';
      position: relative;
      background-image: url(~@/assets/images/icon-arrow-right-white-mobile.svg);
      background-repeat: no-repeat;
      background-size: contain;
      margin-left: 1rem;
      width: 1.7rem;
      min-width: 1.7rem;
      height: 1.3rem;
      min-height: 1.3rem;
      transition: left 0.25s;
      left: 0;
    }

    &:hover,
    &:focus,
    &:active {
      color: $white;
      text-decoration: none;
      background-color: $dark-orange;
    }

    &:hover {
      &:after {
        left: 1rem;
      }
    }

    &.disabled {
      background-color: $gray;
      opacity: 0.6;
      cursor: auto;
    }
  }

  .clear {
    width: auto;
    display: inline-block;
    text-align: left;
    background-color: transparent;
    border: none;
    @include font-styles('Arial Narrow', 2rem, 400, normal, normal, 0, $blue);
  }
}

.back-to-top {
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  padding: 2rem;
  font-size: 16px;
  background-color: $white;
  color: $black;
  border: none;
  border-radius: 4px;
  z-index: 1000;
  cursor: pointer;
  display: block;
  border-top: solid 0.1rem $divider;

  @include tablet-up {
    display: none;
  }
}
</style>
